import { Injectable, ErrorHandler, signal, inject } from '@angular/core';
import { LogService } from '../../services/log.service';

/**
 * ✅ Sprint 1 Extension: Global Error Handler Service
 *
 * Service centralisé pour gérer toutes les erreurs de l'application
 * Remplace le ErrorHandler par défaut d'Angular
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly logService = inject(LogService);
  private _error = signal<Error | null>(null);
  readonly error = this._error.asReadonly();

  // Historique des erreurs (limité à 10)
  private errorHistory: Array<{ error: Error; timestamp: Date }> = [];

  /**
   * Méthode principale appelée par Angular pour toutes les erreurs non gérées
   */
  handleError(error: Error | any): void {
    // Extraire l'erreur réelle si c'est un wrapper Angular
    const originalError = this.extractError(error);

    // Ne pas logger les erreurs réseau en mode hors ligne
    const isOfflineError =
      !navigator.onLine &&
      (originalError.message?.includes('connexion') ||
        originalError.message?.includes('serveur') ||
        originalError.message?.includes('Impossible de contacter'));

    if (!isOfflineError) {
      this.logService.error('Unhandled error', {
        message: originalError.message,
        name: originalError.name,
        stack: originalError.stack,
      });
    }

    // Ajouter à l'historique
    this.addToHistory(originalError);

    // Émettre l'erreur pour que les composants Error Boundary puissent la capturer
    this._error.set(originalError);

    // En production, envoyer à un service de monitoring (Sentry, LogRocket, etc.)
    if (this.isProduction() && !isOfflineError) {
      this.reportToMonitoring(originalError);
    }
  }

  /**
   * Extrait l'erreur originale des wrappers Angular
   */
  private extractError(error: any): Error {
    // Si c'est déjà une Error, la retourner
    if (error instanceof Error) {
      return error;
    }

    // Si c'est un objet avec une propriété error ou rejection
    if (error && (error.error || error.rejection)) {
      return this.extractError(error.error || error.rejection);
    }

    // Sinon créer une nouvelle Error avec le message
    return new Error(error?.message || String(error) || 'Unknown error');
  }

  /**
   * Ajoute une erreur à l'historique
   */
  private addToHistory(error: Error): void {
    this.errorHistory.unshift({
      error,
      timestamp: new Date(),
    });

    // Limiter à 10 erreurs
    if (this.errorHistory.length > 10) {
      this.errorHistory.pop();
    }
  }

  /**
   * Réinitialise l'état d'erreur
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Récupère l'historique des erreurs
   */
  getErrorHistory(): Array<{ error: Error; timestamp: Date }> {
    return [...this.errorHistory];
  }

  /**
   * Vérifie si on est en production
   */
  private isProduction(): boolean {
    return !globalThis.location || globalThis.location.hostname !== 'localhost';
  }

  /**
   * Envoie l'erreur au LogService (batché automatiquement en prod)
   */
  private reportToMonitoring(error: Error): void {
    this.logService.error('Production error', {
      message: error.message,
      stack: error.stack,
      userAgent: navigator.userAgent,
    });
  }

  /**
   * Simule une erreur pour tester le système (dev only)
   */
  simulateError(message: string = 'Test error'): void {
    throw new Error(message);
  }
}
